import {Carousel, useAnimationOffsetEffect} from '@mantine/carousel';
import type {Embla} from '@mantine/carousel';
import {Modal} from '@mantine/core';
import Image from 'next/image';
import {useEffect, useState} from 'react';
import type React from 'react';
import {routes} from '../../../../../../config/constants';
import {
  ENTRY_MANAGEMENTS_GUIDE_1,
  ENTRY_MANAGEMENTS_GUIDE_2,
  ENTRY_MANAGEMENTS_GUIDE_3,
  ENTRY_MANAGEMENTS_GUIDE_4,
  ENTRY_MANAGEMENTS_GUIDE_5,
  ENTRY_MANAGEMENTS_GUIDE_SP_GUIDE_1,
  ENTRY_MANAGEMENTS_GUIDE_SP_GUIDE_2,
  ENTRY_MANAGEMENTS_GUIDE_SP_GUIDE_3,
  ENTRY_MANAGEMENTS_GUIDE_SP_GUIDE_4,
  ENTRY_MANAGEMENTS_GUIDE_SP_GUIDE_5,
} from '../../../../../../config/constants/img';
import {cn} from '../../../../../../lib/classname';
import {CommonLink} from '../../../../../../shared/components/CommonLink';
import {ENTRY_MANAGEMENT_FEATURE_NAME} from '../../../../constants/metaData';
import {Z_INDEX} from '../../../../constants/zIndex';
import {ModalCancelButton} from '../ModalCancelButton';
import {ModalSubmitButton} from '../ModalSubmitButton';

type Props = {
  opened: boolean;
  onClose: () => void;
  onAddEntry?: () => void;
  isSP?: boolean;
  /**
   * ログイン状態をチェックし、未ログイン時はモーダルを表示する
   * @returns ログイン済みならtrue、未ログインならfalse
   */
  requireAuth?: () => boolean;
  /**
   * 大学認証状態をチェックし、未認証時はモーダルを表示する
   * @returns 大学認証済みならtrue、未認証ならfalse
   */
  requireStudentVerification?: () => boolean;
};

/**
 * 画像の設定情報
 */
type ImageConfig = {
  src: string;
  width: number;
  height: number;
};

/**
 * ガイドの各セクション情報
 */
type GuideSection = {
  title: string;
  description: string | React.ReactNode;
  image?: ImageConfig;
};

// モーダルの高さ設定
const MODAL_HEIGHT = {
  PC: 494,
  SP: 548,
} as const;

/**
 * **text** 形式のマークダウン風記法を太字に変換する
 *
 * @param text - 変換対象のテキスト（**で囲まれた部分が太字になる）
 * @returns 太字化されたReact要素、または元のテキスト
 *
 * @example
 * parseDescription("これは**太字**です")
 * // => ["これは", <span className="font-bold">太字</span>, "です"]
 *
 * @example
 * parseDescription("") // => ""
 * parseDescription("通常のテキスト") // => "通常のテキスト"
 */
const parseDescription = (text: string): React.ReactNode => {
  // 空文字列または通常のテキストの場合はそのまま返す
  if (!text || !text.includes('**')) {
    return text;
  }

  const parts = text.split(/(\*\*[^*]+\*\*)/g);

  // 分割結果が1つだけの場合（**が含まれない場合）はそのまま返す
  if (parts.length === 1) {
    return text;
  }

  return parts.map((part, index) => {
    // 空文字列の場合はスキップ
    if (!part) {
      return null;
    }

    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2);
      // 太字部分が空の場合は何も返さない
      if (!boldText) {
        return null;
      }
      return (
        <span key={index} className="font-bold">
          {boldText}
        </span>
      );
    }
    return part;
  });
};

/**
 * 共通テキスト定数（title, description）
 * descriptionで **text** と記述すると太字になります
 *
 * @remarks
 * PC版とSP版で同じテキストを使用し、画像のみを切り替えることで
 * テキストの重複を削減し、メンテナンス性を向上させています。
 */
const GUIDE_TEXTS = [
  {
    title: `${ENTRY_MANAGEMENT_FEATURE_NAME}へようこそ`,
    description: `${ENTRY_MANAGEMENT_FEATURE_NAME}はあなたの**就職活動を一元管理**し、選考状況を俯瞰して把握したり、**AIによる外資就活に蓄積された情報をもとに選考ステップの提示や次にやるべきことの提案**をうけられ、**カレンダーでスケジュールも管理**ができるまったく新しい機能です。あなたの就職活動のよきパートナーとして活用してください！`,
  },
  {
    title: '選考管理機能',
    description:
      '企業別に選考状況を管理できます。外資就活上でのエントリー有無を問わず管理できる設計にしています。体験記を元に**今後の選考ステップも予測して提示**するので、対策や計画を立てやすくなります。各選考ステップではAI選考対策に加え、AI ToDo自動生成機能を活用することで、**AIが選考体験記を参照してステップ内容に最適化されたToDoを自動生成・提案**し、あなたの就職活動に伴走します。',
  },
  {
    title: 'AI ToDo管理機能',
    description:
      '選考管理機能に追加された内容をもとに、ステップに応じたやることリストを作成できます。**AIによる企業別に特化した詳細なToDo自動生成機能**もあるので、次になにをしたらいいのかわからない時や、忙しい時でも大丈夫です。ぜひ試してみてください。',
  },
  {
    title: 'スケジュール管理機能',
    description:
      '選考におけるさまざまな締め切りや、大切な面接の予定をカレンダーで管理できます。ToDoも表示されるので、今やるべきことも同時に把握できます。**Googleカレンダーと連携**もできます。',
  },
  {
    title: '便利な入力方法でどこよりもかんたんに追加！',
    description:
      '外資就活ドットコムに掲載されている募集から予定を追加したり、エントリーすると自動で追加されるので、おすすめです。また、外資就活以外の選考予定も**スクリーンショットなど画像をアップロードしたり、メールの文面をコピーするだけでAIが解析して自動入力**してくれます。',
  },
  {
    title: '早速使ってみよう！',
    description: '気になる募集を探して予定を追加するかエントリーをしてみよう!',
  },
] as const;

/**
 * PC用画像設定
 * 各インデックスはGUIDE_TEXTSの対応するセクションと一致している必要があります
 * 最終ステップは画像なしのためundefinedを指定
 */
const GUIDE_IMAGES_PC: (ImageConfig | undefined)[] = [
  {src: ENTRY_MANAGEMENTS_GUIDE_1, width: 280, height: 104},
  {src: ENTRY_MANAGEMENTS_GUIDE_2, width: 434, height: 135},
  {src: ENTRY_MANAGEMENTS_GUIDE_3, width: 434, height: 116},
  {src: ENTRY_MANAGEMENTS_GUIDE_4, width: 576, height: 120},
  {src: ENTRY_MANAGEMENTS_GUIDE_5, width: 280, height: 72},
  undefined,
];

/**
 * SP用画像設定
 * 各インデックスはGUIDE_TEXTSの対応するセクションと一致している必要があります
 * 最終ステップは画像なしのためundefinedを指定
 */
const GUIDE_IMAGES_SP: (ImageConfig | undefined)[] = [
  {src: ENTRY_MANAGEMENTS_GUIDE_SP_GUIDE_1, width: 280, height: 104},
  {src: ENTRY_MANAGEMENTS_GUIDE_SP_GUIDE_2, width: 314, height: 96},
  {src: ENTRY_MANAGEMENTS_GUIDE_SP_GUIDE_3, width: 314, height: 84},
  {src: ENTRY_MANAGEMENTS_GUIDE_SP_GUIDE_4, width: 144, height: 120},
  {src: ENTRY_MANAGEMENTS_GUIDE_SP_GUIDE_5, width: 280, height: 72},
  undefined,
];

/**
 * GUIDE_TEXTSと画像設定からGuideSectionを生成する
 *
 * @param images - PC用またはSP用の画像設定配列
 * @returns テキストと画像を組み合わせたGuideSectionの配列
 *
 * @remarks
 * 開発時には配列長の検証を行い、設定ミスを早期に検出します。
 * GUIDE_TEXTSと画像配列の要素数が一致していない場合は警告を出力します。
 */
const createGuideSections = (
  images: (ImageConfig | undefined)[],
): GuideSection[] => {
  // 開発時のみ配列長の検証を実行
  if (process.env.NODE_ENV === 'development') {
    if (GUIDE_TEXTS.length !== images.length) {
      console.warn(
        `[GuideModal] GUIDE_TEXTSと画像配列の要素数が一致していません。GUIDE_TEXTS: ${GUIDE_TEXTS.length}, images: ${images.length}`,
      );
    }
  }

  return GUIDE_TEXTS.map((text, index) => ({
    title: text.title,
    description: parseDescription(text.description),
    image: images[index],
  }));
};

/**
 * PC版ガイドセクション
 * PC用の画像サイズで構成されたガイド情報
 */
const GUIDE_SECTIONS: GuideSection[] = createGuideSections(GUIDE_IMAGES_PC);

/**
 * SP版ガイドセクション
 * SP用の画像サイズで構成されたガイド情報
 */
const GUIDE_SECTIONS_SP: GuideSection[] = createGuideSections(GUIDE_IMAGES_SP);

/**
 * ガイドコンテンツコンポーネントの共通プロップス型定義
 */
type GuideContentProps = {
  section: GuideSection;
  index: number;
  totalSteps: number;
  onClose: () => void;
  onAddEntry?: () => void;
  requireAuth?: () => boolean;
  requireStudentVerification?: () => boolean;
};

/**
 * ステップインジケーターコンポーネント
 *
 * @remarks
 * 現在のステップ位置を視覚的に表示する丸いインジケーター。
 * 現在のステップは`bg-primary`で、それ以外は`bg-black-4`で表示されます。
 */
const StepIndicator: React.FC<{
  currentStep: number;
  totalSteps: number;
}> = ({currentStep, totalSteps}) => {
  return (
    <div className="flex flex-col items-center gap-8">
      <div className="flex gap-8">
        {Array.from({length: totalSteps}).map((_, index) => (
          <div
            key={`indicator-${index}`}
            className={cn('w-12 h-12 rounded-full transition-colors', {
              'bg-primary': index === currentStep,
              'bg-black-4': index !== currentStep,
            })}
            aria-label={`${index + 1}${index === currentStep ? '（現在）' : ''}`}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * 最終ステップのアクションボタン群
 *
 * @remarks
 * ガイドの最終ステップで表示される2つのアクションボタン:
 * - 募集を探す（外部リンク）
 * - 企業の選考予定を追加する（モーダル内遷移 or ページ遷移）
 *
 * `onAddEntry`プロップの有無により、ボタンの種類（ModalCancelButton or CommonLink）が切り替わります。
 * 未ログイン時は`requireAuth`で会員登録導線モーダル、大学認証が未完了なら
 * `requireStudentVerification`で大学認証モーダルを表示します。
 */
const LastStepActions: React.FC<{
  onClose: () => void;
  onAddEntry?: () => void;
  requireAuth?: () => boolean;
  requireStudentVerification?: () => boolean;
}> = ({onClose, onAddEntry, requireAuth, requireStudentVerification}) => {
  const handleAddEntry = () => {
    // 未ログイン時は会員登録導線モーダルを表示
    if (requireAuth && !requireAuth()) {
      onClose();
      return;
    }
    // 大学認証が未完了の場合は大学認証モーダルを表示
    if (requireStudentVerification && !requireStudentVerification()) {
      onClose();
      return;
    }
    if (onAddEntry) {
      onAddEntry();
      onClose();
    }
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    // 未ログイン時は遷移をキャンセルして会員登録導線モーダルを表示
    if (requireAuth && !requireAuth()) {
      e.preventDefault();
      onClose();
      return;
    }
    // 大学認証が未完了の場合は遷移をキャンセルして大学認証モーダルを表示
    if (requireStudentVerification && !requireStudentVerification()) {
      e.preventDefault();
      onClose();
      return;
    }
    onClose();
  };

  return (
    <div className="flex flex-col items-center justify-center gap-16">
      <CommonLink
        href={routes.RECRUITING_INFO}
        target="_blank"
        className="w-[200px] text-base text-center font-bold rounded-full px-16 py-[10px] border-[2px] border-primary bg-primary text-white"
      >
        募集を探す
      </CommonLink>
      {onAddEntry ? (
        <ModalCancelButton className="w-[220px]" onClick={handleAddEntry}>
          企業の選考予定を追加する
        </ModalCancelButton>
      ) : (
        <CommonLink
          href={routes.ENTRY_MANAGEMENTS_PROGRESS}
          className="w-[220px] text-base text-center font-bold rounded-full px-16 py-[10px] border-[2px] border-primary bg-white text-primary"
          onClick={handleLinkClick}
        >
          企業の選考予定を追加する
        </CommonLink>
      )}
    </div>
  );
};

/**
 * ガイドコンテンツ共通部分
 *
 * @remarks
 * PC版とSP版で共通のガイドコンテンツレイアウト。
 * - タイトル
 * - 説明文（parseDescriptionで太字処理済み）
 * - 画像（存在する場合のみ）
 * - 最終ステップの場合はアクションボタン
 *
 * PC版とSP版の両方で使用されるため、レイアウトの一貫性を保証します。
 */
const GuideContent: React.FC<{
  section: GuideSection;
  isLast: boolean;
  onClose: () => void;
  onAddEntry?: () => void;
  requireAuth?: () => boolean;
  requireStudentVerification?: () => boolean;
}> = ({
  section,
  isLast,
  onClose,
  onAddEntry,
  requireAuth,
  requireStudentVerification,
}) => {
  return (
    <>
      <div className="flex flex-col gap-8">
        <h4 className="text-xl font-bold mb-8">{section.title}</h4>
        <p className="text-base pc:text-md text-text-2 text-justify break-words">
          {section.description}
        </p>
      </div>
      {section.image && (
        <div className="flex items-center justify-center">
          <Image
            src={section.image.src}
            width={section.image.width}
            height={section.image.height}
            alt={section.title}
            className="object-contain max-h-full"
          />
        </div>
      )}
      {isLast && (
        <LastStepActions
          onClose={onClose}
          onAddEntry={onAddEntry}
          requireAuth={requireAuth}
          requireStudentVerification={requireStudentVerification}
        />
      )}
    </>
  );
};

/**
 * PC版ガイドコンテンツコンポーネント
 *
 * @remarks
 * PC版では単一のコンテンツを表示するシンプルなラッパー。
 * GuideContentコンポーネントを使用して一貫性を保ちます。
 */
const PCContent: React.FC<GuideContentProps> = ({
  section,
  index,
  totalSteps,
  onClose,
  onAddEntry,
  requireAuth,
  requireStudentVerification,
}) => {
  const isLast = index === totalSteps - 1;

  return (
    <GuideContent
      section={section}
      isLast={isLast}
      onClose={onClose}
      onAddEntry={onAddEntry}
      requireAuth={requireAuth}
      requireStudentVerification={requireStudentVerification}
    />
  );
};

/**
 * SP版ガイドスライドコンポーネント
 *
 * @remarks
 * SP版ではCarouselのスライドとして使用されるため、
 * スクロール制御用の追加レイアウトを持ちます。
 * 下部のナビゲーションボタンエリア（140px）を考慮したパディングを設定。
 */
const SPSlide: React.FC<GuideContentProps> = ({
  section,
  index,
  totalSteps,
  onClose,
  onAddEntry,
  requireAuth,
  requireStudentVerification,
}) => {
  const isLast = index === totalSteps - 1;

  return (
    <div className="flex flex-col h-full px-16 pb-[140px] overflow-hidden">
      <div className="flex-shrink-0 flex flex-col gap-16 overflow-hidden">
        <GuideContent
          section={section}
          isLast={isLast}
          onClose={onClose}
          onAddEntry={onAddEntry}
          requireAuth={requireAuth}
          requireStudentVerification={requireStudentVerification}
        />
      </div>
    </div>
  );
};

/**
 * 進捗管理の使い方を説明するガイドモーダル（ウォークスルー形式）
 *
 * @remarks
 * エントリー管理機能の初回利用時などに表示される、ステップ形式のチュートリアルモーダル。
 *
 * **主な機能:**
 * - PC/SPで異なるレイアウト（PC: ステップ切り替え、SP: Carouselスワイプ）
 * - 6ステップのガイド表示
 * - 最終ステップでのアクションボタン（募集を探す / 選考予定を追加）
 *
 * **実装の特徴:**
 * - GUIDE_TEXTSとGUIDE_IMAGES_PC/SPの組み合わせで重複を削減
 * - parseDescription関数による**太字**記法のサポート
 * - Embla Carouselによる滑らかなスライド遷移（SP版）
 * - モーダル開閉時の状態リセット
 *
 * @param props.opened - モーダルの開閉状態
 * @param props.onClose - モーダルを閉じる際のコールバック
 * @param props.onAddEntry - 「選考予定を追加」ボタン押下時のコールバック（オプション）
 * @param props.isSP - SP表示かどうか（デフォルト: false）
 */
export const GuideModal: React.FC<Props> = ({
  opened,
  onClose,
  onAddEntry,
  isSP = false,
  requireAuth,
  requireStudentVerification,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [embla, setEmbla] = useState<Embla | null>(null);
  const sections = isSP ? GUIDE_SECTIONS_SP : GUIDE_SECTIONS;
  const totalSteps = sections.length;

  // Modal内のCarousel位置ずれ対策（300msはModalのアニメーション時間）
  useAnimationOffsetEffect(embla, 300);

  // Embla APIのイベントリスナー設定
  useEffect(() => {
    if (!embla) return;

    const onSelect = () => {
      setCurrentStep(embla.selectedScrollSnap());
    };

    // 初期状態を同期
    setCurrentStep(embla.selectedScrollSnap());

    embla.on('select', onSelect);
    return () => {
      embla.off('select', onSelect);
    };
  }, [embla]);

  // モーダルが開かれた時にステップをリセット
  useEffect(() => {
    if (opened) {
      setCurrentStep(0);
      embla?.scrollTo(0, true); // 即座に最初のスライドへ
    }
  }, [opened, embla]);

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep); // 状態を先に更新
      if (isSP && embla) {
        embla.scrollTo(nextStep); // SP版はアニメーションも実行
      }
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep); // 状態を先に更新
      if (isSP && embla) {
        embla.scrollTo(prevStep); // SP版はアニメーションも実行
      }
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="xl"
      radius={16}
      centered
      zIndex={Z_INDEX.MODAL}
      styles={{
        inner: {
          left: 0,
          right: 0,
          margin: 'auto',
        },
      }}
    >
      <div
        className={cn('flex flex-col justify-between', {
          'p-16': !isSP,
        })}
        style={{height: isSP ? MODAL_HEIGHT.SP : MODAL_HEIGHT.PC}}
      >
        {/* SP: Carousel、PC: 通常表示 */}
        {isSP ? (
          <div className="relative h-full">
            <Carousel
              getEmblaApi={setEmbla}
              withControls={false}
              withIndicators={false}
              slideGap={0}
              draggable
              loop={false}
              className="h-full"
            >
              {sections.map((section, index) => (
                <Carousel.Slide key={`guide-slide-${section.title}-${index}`}>
                  <SPSlide
                    section={section}
                    index={index}
                    totalSteps={totalSteps}
                    onClose={handleClose}
                    onAddEntry={onAddEntry}
                    requireAuth={requireAuth}
                    requireStudentVerification={requireStudentVerification}
                  />
                </Carousel.Slide>
              ))}
            </Carousel>

            {/* インジケータ・ボタンを絶対配置でオーバーレイ */}
            <div className="absolute bottom-0 left-0 right-0 px-16 pb-16 pointer-events-none">
              <div className="flex flex-col gap-16 pointer-events-auto">
                {/* ステップインジケーター */}
                <StepIndicator
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                />

                {/* ナビゲーションボタン */}
                <div className="flex gap-8 justify-end">
                  <div>
                    {currentStep > 0 && (
                      <ModalCancelButton onClick={handlePrev}>
                        戻る
                      </ModalCancelButton>
                    )}
                  </div>
                  <div className="flex pc:flex-row flex-col items-center gap-8">
                    {currentStep < totalSteps - 1 ? (
                      <ModalSubmitButton onClick={handleNext}>
                        次へ
                      </ModalSubmitButton>
                    ) : (
                      <ModalSubmitButton onClick={handleClose}>
                        完了
                      </ModalSubmitButton>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {sections[currentStep] && (
              <PCContent
                section={sections[currentStep]}
                index={currentStep}
                totalSteps={totalSteps}
                onClose={handleClose}
                onAddEntry={onAddEntry}
                requireAuth={requireAuth}
                requireStudentVerification={requireStudentVerification}
              />
            )}

            <div className="flex flex-col gap-16 justify-between">
              {/* ステップインジケーター */}
              <StepIndicator
                currentStep={currentStep}
                totalSteps={totalSteps}
              />

              {/* ナビゲーションボタン */}
              <div className="flex gap-8 justify-end">
                <div>
                  {currentStep > 0 && (
                    <ModalCancelButton onClick={handlePrev}>
                      戻る
                    </ModalCancelButton>
                  )}
                </div>
                <div className="flex pc:flex-row flex-col items-center gap-8">
                  {currentStep < totalSteps - 1 ? (
                    <ModalSubmitButton onClick={handleNext}>
                      次へ
                    </ModalSubmitButton>
                  ) : (
                    <ModalSubmitButton onClick={handleClose}>
                      完了
                    </ModalSubmitButton>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
